import { CircleCheck, TriangleAlert } from "lucide-react";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { getErrorMessage } from "@/core/api/errors";
import { dayjs } from "@/core/utils/date";
import {
  useApprovePay,
  useGetPackApproval,
  useWithdrawPayApproval,
} from "@/payroll/pack-approval/query";
import { usePaypacksAccess } from "@/payroll/pay-breakdown/payroll-emails-access";

interface PayApprovalPanelProps {
  businessId: string | null;
  runId: string;
  /** A paid period can no longer be approved or unapproved. */
  isLocked: boolean;
  /** The statement on screen, so the approval is re-checked when its figures move. */
  statement?: unknown;
}

/**
 * The technician saying their own pay looks right.
 *
 * One tap, and deliberately so. This is a technician telling their manager the
 * numbers look right — not a signature, not an agreement to anything, and nothing
 * they need to be walked through first. It went through a spell with a notice and a
 * confirmation step behind it; that made a small, friendly act feel like signing
 * something, which is the opposite of what gets people to use it.
 *
 * Sits at the foot of the pack, beside the box where they would say it does NOT look
 * right, because the two are the same decision: a technician who has read their
 * figures either agrees with them or has a question.
 *
 * Renders nothing at all until the endpoint has answered. An "Approve my pay" button
 * that turns out to have been unavailable — the business has no saved PayPacks access, the query
 * failed — is worse than a footer that simply never offered one.
 */
export const PayApprovalPanel = ({
  businessId,
  runId,
  isLocked,
  statement,
}: PayApprovalPanelProps) => {
  const { enabled: approvalsEnabled, isLoading: productsLoading } =
    usePaypacksAccess();
  const { data, isLoading, isError } = useGetPackApproval(
    businessId,
    runId,
    !productsLoading && approvalsEnabled,
    { followsStatement: statement },
  );
  const approve = useApprovePay(businessId, runId);
  const withdraw = useWithdrawPayApproval(businessId, runId);

  if (productsLoading || !approvalsEnabled || isLoading || isError || !data) {
    return null;
  }

  const approval = data.approval;
  const pending = approve.isPending || withdraw.isPending;

  const onApprove = () =>
    approve.mutate(undefined, {
      onError: (error) => toast.error(getErrorMessage(error)),
    });

  // Approved, but a rerun or an edit has moved the figures since. Said plainly, with
  // the way to agree to the new ones: a plain tick here would tell a technician they
  // had approved numbers they have never seen.
  //
  // Only on a definite `false`. Where the comparison could not be made at all it says
  // nothing and shows the standing approval below — "your pay has changed" is a claim
  // about somebody's own pay, and making it on a comparison nobody could make is how
  // technicians were told to re-approve pay that had not moved (ENG-2511).
  if (approval && approval.current === false) {
    return (
      <Stack
        fullWidth
        gap="sm"
        className="border-t border-stroke-tertiary pt-5"
      >
        <Stack direction="row" align="center" gap="xs" className="min-w-0">
          <TriangleAlert className="size-4 shrink-0 text-gold-70" aria-hidden />
          <Typography variant="subtitleW3">Your pay has changed</Typography>
        </Stack>
        <Typography variant="bodyW3" textColor="secondary">
          You approved an earlier version of this pay on{" "}
          {dayjs(approval.approved_at).format("MMM D")}. These figures have been
          updated since.
        </Typography>
        {!isLocked && (
          <Button
            variant="secondary"
            fullWidth
            loading={pending}
            onClick={onApprove}
          >
            Approve the updated pay
          </Button>
        )}
      </Stack>
    );
  }

  // Approved, and the API cannot check that against the figures on screen. "You
  // approved this pay" over a number nobody can tie to their sign-off is the same
  // unsupported claim as the warning above, pointing the other way — so it says what
  // is actually known: there is an approval, and whether it covers this is unknown. No
  // button: nothing here shows their pay changed, and asking for a second sign-off on
  // that basis is the nag ENG-2511 is about.
  if (approval && approval.current === null) {
    return (
      <Stack
        fullWidth
        gap="xs"
        className="border-t border-stroke-tertiary pt-5"
      >
        <Stack direction="row" align="center" gap="xs" className="min-w-0">
          <CircleCheck
            className="size-4 shrink-0 text-tertiary-text"
            aria-hidden
          />
          <Typography variant="subtitleW3">Approval on file</Typography>
        </Stack>
        <Typography variant="bodyW3" textColor="secondary">
          You approved your pay on{" "}
          {dayjs(approval.approved_at).format("MMM D, h:mm a")}. We cannot tell
          whether these figures have changed since.
        </Typography>
        {!isLocked && (
          <Button
            variant="text"
            size="xs"
            className="w-fit px-0"
            loading={pending}
            onClick={() =>
              withdraw.mutate(undefined, {
                onError: (error) => toast.error(getErrorMessage(error)),
              })
            }
          >
            Undo
          </Button>
        )}
      </Stack>
    );
  }

  if (approval) {
    return (
      <Stack
        fullWidth
        gap="xs"
        className="border-t border-stroke-tertiary pt-5"
      >
        <Stack direction="row" align="center" gap="xs" className="min-w-0">
          <CircleCheck className="size-4 shrink-0 text-primary" aria-hidden />
          <Typography variant="subtitleW3">You approved this pay</Typography>
        </Stack>
        <Typography variant="bodyW3" textColor="secondary">
          Approved {dayjs(approval.approved_at).format("MMM D, h:mm a")}. Your
          manager can see that you approved it.
        </Typography>
        {!isLocked && (
          <Button
            variant="text"
            size="xs"
            className="w-fit px-0"
            loading={pending}
            onClick={() =>
              withdraw.mutate(undefined, {
                onError: (error) => toast.error(getErrorMessage(error)),
              })
            }
          >
            Undo
          </Button>
        )}
      </Stack>
    );
  }

  // Nothing to approve on a period that is already paid, and no way to change it
  // if there were.
  if (isLocked) return null;

  return (
    <Stack fullWidth gap="xs" className="border-t border-stroke-tertiary pt-5">
      <Button
        variant="secondary"
        fullWidth
        loading={pending}
        leftIcon={<CircleCheck className="h-4 w-4" />}
        onClick={onApprove}
      >
        Approve my pay
      </Button>
      <Typography variant="bodyW3" textColor="tertiary">
        Lets your manager know these figures look right to you. It doesn't
        change your pay, and you can undo it.
      </Typography>
    </Stack>
  );
};
